import { logError, logWarn } from '@ptek/client-logger';
import { SessionDocument } from '../api-clients/sessions';
import { TranscriptLine } from '../types/recorder';

export interface ParsedTranscriptSegment {
  id: string;
  timestamp: number;
  text: string;
  confidence: number;
  speaker: 'provider' | 'patient' | 'mixed';
}

export interface ParsedTranscriptData {
  segments: ParsedTranscriptSegment[];
  summary?: {
    segmentCount: number;
    wordCount: number;
    speakerSegmentCounts: Record<string, number>;
    whisperSessionId: string;
  };
}

interface RawTranscriptSegment {
  id?: string;
  timestamp?: string;
  text?: string;
  confidence?: number;
  speaker?: string;
}

interface RawTranscriptContent {
  segments: RawTranscriptSegment[];
  summary?: {
    segmentCount: number;
    wordCount: number;
    speakerSegmentCounts: Record<string, number>;
    whisperSessionId: string;
  };
}

export class TranscriptProcessor {
  static formatTimestamp(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  static parseTimestampToSeconds(timestamp: string): number {
    try {
      const parts = timestamp.split(':');
      if (parts.length !== 3) return 0;

      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseInt(parts[2], 10) || 0;

      return hours * 3600 + minutes * 60 + seconds;
    } catch {
      return 0;
    }
  }

  static convertToUploadFormat(lines: TranscriptLine[]): Array<{
    id: string;
    speaker: string;
    timestamp: string;
    text: string;
    confidence: number;
  }> {
    return lines.map((line, index) => ({
      id: `segment-${index}`,
      timestamp: TranscriptProcessor.formatTimestamp(line.timestamp),
      text: line.text,
      speaker: 'provider',
      confidence: line.confidence,
    }));
  }

  static parseTranscriptContent(content: unknown): ParsedTranscriptData | null {
    try {
      const parsed: RawTranscriptContent =
        typeof content === 'string'
          ? JSON.parse(content)
          : (content as RawTranscriptContent);

      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !Array.isArray(parsed.segments)
      ) {
        logWarn(
          'TranscriptProcessor',
          'Invalid transcript format: missing segments array'
        );
        return null;
      }

      const segments: ParsedTranscriptSegment[] = parsed.segments.map(
        (segment) => ({
          id: segment.id || 'unknown',
          timestamp: TranscriptProcessor.parseTimestampToSeconds(
            segment.timestamp || '00:00:00'
          ),
          text: segment.text || '',
          confidence: segment.confidence || 0.9,
          speaker: 'provider',
        })
      );

      return {
        segments,
        summary: parsed.summary,
      };
    } catch (error) {
      logError(
        'TranscriptProcessor',
        'Failed to parse transcript content:',
        error
      );
      return null;
    }
  }

  /**
   * Converts a transcript array to plain text by joining all text fields with newlines.
   * Returns undefined if transcript is empty or undefined.
   */
  static toPlainText(
    transcript: Array<{ text: string }> | undefined
  ): string | undefined {
    if (!transcript || transcript.length === 0) {
      return undefined;
    }
    return transcript
      .map((line) => line.text)
      .filter((text) => text != null)
      .join('\n');
  }

  static toPlainTextWithOverrides(
    transcript: Array<{ text: string; segmentId?: string }> | undefined,
    editOverrides?: Record<string, { text: string }>
  ): string | undefined {
    if (!transcript || transcript.length === 0) {
      return undefined;
    }
    if (editOverrides?.__fullText__?.text) {
      return editOverrides.__fullText__.text;
    }
    return transcript
      .map((line, index) => {
        const segId = line.segmentId || `segment-${index}`;
        return editOverrides?.[segId]?.text ?? line.text;
      })
      .filter((text) => text != null)
      .join(' ');
  }
}

export function getTranscriptDocument(
  documents: SessionDocument[]
): SessionDocument | null {
  return (
    documents.find(
      (doc) => doc.type === 'transcript' || doc.documentType === 'transcript'
    ) || null
  );
}

export function getGeneratedDocuments(
  documents: SessionDocument[]
): SessionDocument[] {
  return documents.filter(
    (doc) => doc.type !== 'transcript' && doc.documentType !== 'transcript'
  );
}
